import { supabase } from '../utils/supabase';
import type {
  EquipmentStandard,
  WorkScenario,
  ScenarioImprovement,
  CreateScenarioRequest,
  CreateImprovementRequest,
  ScenarioWithImprovements,
  EquipmentsByCategory,
  EquipmentCategory,
} from '../types/work-scenarios';
import {
  calculateWindowsImprovement,
  calculateWallInsulationImprovement,
  calculateRoofInsulationImprovement,
  calculateFloorInsulationImprovement,
  calculateHeatingSystemImprovement,
  calculateHotWaterSystemImprovement,
  calculateVentilationImprovement,
  calculateHeatingControlImprovement,
  calculateScenarioTotals,
  calculateProjectedDPELabel,
  calculateProjectedDPELabelFromEP,
  calculateProjectedPrimaryEnergy,
  calculateProjectedGESLabel,
  calculateTotalGESWithImprovements,
} from '../utils/work-scenarios-calculator';
import { recalculateWithCumulativeEffects } from '../utils/scenario-cumulative-calculator';
import { enrichDPEWithEstimates } from '../utils/dpe-fallback-estimator';
import {
  generateFilteredRecommendations,
  normalizeAnswers,
  isWorkTypeApplicable,
  filterHeatingEquipments,
  filterHotWaterEquipments,
  filterWindowsEquipments,
  filterWallInsulationEquipments,
  filterRoofInsulationEquipments,
  normalizeAnswers as normalizeUserAnswers
} from '../utils/work-recommendations-filter';
import { 
  normalizeDPEData,
  NormalizedDPEData 
} from '../utils/dpe-data-normalizer';
import { calculateDPEProjection } from '../utils/dpe-projections';

export async function fetchEquipmentStandards(): Promise<EquipmentStandard[]> {
  const { data, error } = await supabase
    .from('equipment_standards')
    .select('*')
    .eq('is_active', true)
    .order('category')
    .order('display_order');

  if (error) {
    throw new Error(`Failed to fetch equipment standards: ${error.message}`);
  }

  return data || [];
}

export async function fetchEquipmentsByCategory(): Promise<EquipmentsByCategory> {
  const standards = await fetchEquipmentStandards();

  const grouped: EquipmentsByCategory = {
    windows: [],
    wall_insulation: [],
    roof_insulation: [],
    floor_insulation: [],
    heating: [],
    hot_water: [],
    ventilation: [],
    heating_control: [],
  };

  standards.forEach((standard) => {
    const category = standard.category as EquipmentCategory;
    if (grouped[category]) {
      grouped[category].push(standard);
    }
  });

  return grouped;
}

/**
 * Récupère les équipements filtrés selon les données DPE et les réponses utilisateur
 * VERSION 2 - Compatible avec DPE legacy et nouveaux DPE
 */
export async function fetchApplicableEquipmentsByCategory(
  dpeId: string
): Promise<{
  equipments: EquipmentsByCategory;
  recommendations: any[];
  blockedCategories: Record<string, string[]>;
}> {
  const { data: rawDpeData, error: dpeError } = await supabase
    .from('dpe_data')
    .select('*')
    .eq('id', dpeId)
    .maybeSingle();

  if (dpeError || !rawDpeData) {
    throw new Error('DPE data not found');
  }

  const normalizedDPE: NormalizedDPEData = normalizeDPEData(rawDpeData);
  const enrichedDPE = enrichDPEWithEstimates(normalizedDPE as any);

  const { data: sessionData } = await supabase
    .from('questionnaire_sessions')
    .select('answers_json')
    .eq('dpe_id', dpeId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  const userAnswers = sessionData?.answers_json || {};
  const normalizedAnswers = normalizeUserAnswers(userAnswers);

  console.log('DEBUG: Work scenarios service - DPE processing:', {
    hasEstimatedData: enrichedDPE.has_estimated_data,
    deperditions_murs: enrichedDPE.deperditions_murs,
    besoin_chauffage: enrichedDPE.besoin_chauffage,
    isLegacyDPE: enrichedDPE.has_estimated_data === true
  });

  const recommendations = generateFilteredRecommendations(enrichedDPE as any, normalizedAnswers);

  console.log('DEBUG: Generated recommendations:', {
    total: recommendations.length,
    feasible: recommendations.filter(r => r.isFeasible).length,
    categories: recommendations.map(r => ({
      category: r.category,
      isFeasible: r.isFeasible,
      priority: r.priority,
      blockers: r.blockers
    }))
  });

  const allEquipments = await fetchEquipmentsByCategory();

  const blockedCategories: Record<string, string[]> = {};
  recommendations
    .filter(rec => !rec.isFeasible && rec.blockers)
    .forEach(rec => {
      blockedCategories[rec.category] = rec.blockers || [];
    });

  const applicableCategories = new Set(
    recommendations
      .filter(rec => rec.isFeasible)
      .map(rec => rec.category)
  );

  const filteredEquipments: EquipmentsByCategory = {
    windows: applicableCategories.has('windows')
      ? filterWindowsEquipments(allEquipments.windows, enrichedDPE as any, normalizedAnswers)
      : [],
    wall_insulation: applicableCategories.has('wall_insulation')
      ? filterWallInsulationEquipments(allEquipments.wall_insulation, enrichedDPE as any, normalizedAnswers)
      : [],
    roof_insulation: applicableCategories.has('roof_insulation')
      ? filterRoofInsulationEquipments(allEquipments.roof_insulation, enrichedDPE as any, normalizedAnswers)
      : [],
    floor_insulation: applicableCategories.has('floor_insulation')
      ? allEquipments.floor_insulation
      : [],
    heating: applicableCategories.has('heating')
      ? filterHeatingEquipments(allEquipments.heating, enrichedDPE as any, normalizedAnswers)
      : [],
    hot_water: applicableCategories.has('hot_water')
      ? filterHotWaterEquipments(allEquipments.hot_water, enrichedDPE as any, normalizedAnswers)
      : [],
    ventilation: applicableCategories.has('ventilation')
      ? allEquipments.ventilation
      : [],
    heating_control: applicableCategories.has('heating_control')
      ? allEquipments.heating_control
      : [],
  };

  return {
    equipments: filteredEquipments,
    recommendations,
    blockedCategories
  };
}

export async function fetchEquipmentStandard(id: string): Promise<EquipmentStandard | null> {
  const { data, error } = await supabase
    .from('equipment_standards')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch equipment standard: ${error.message}`);
  }

  return data;
}

export async function createWorkScenario(
  request: CreateScenarioRequest
): Promise<WorkScenario> {
  const { data: dpeData, error: dpeError } = await supabase
    .from('dpe_data')
    .select('*')
    .eq('id', request.dpe_id)
    .maybeSingle();

  if (dpeError || !dpeData) {
    throw new Error('DPE not found');
  }

  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    throw new Error('User not authenticated');
  }

  // IMPORTANT: Ne pas recalculer les classes actuelles du DPE
  // Les classes energy_class et climate_class proviennent du DPE officiel certifié ADEME
  // et ont déjà été ajustées lors de la récupération des données.
  // On les utilise telles quelles pour représenter l'état actuel du logement.

  // Utiliser la consommation du DPE officiel (déjà par m²)
  const currentConsumption = dpeData.primary_energy_consumption || dpeData.conso_5_usages_par_m2_ep || 0;

  // Utiliser les émissions GES du DPE officiel (déjà par m²)
  const currentGESEmissions = dpeData.greenhouse_gas_emissions || dpeData.emission_ges_5_usages_par_m2 || 0;

  const { data, error } = await supabase
    .from('work_scenarios')
    .insert({
      user_id: userData.user.id,
      dpe_id: request.dpe_id,
      name: request.name || 'Scénario de travaux',
      description: request.description,
      current_energy_consumption: currentConsumption,
      current_energy_class: dpeData.energy_class,
      current_ges_emissions: currentGESEmissions,
      current_climate_class: dpeData.climate_class,
      status: 'draft',
    })
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create work scenario: ${error.message}`);
  }

  return data;
}

export async function addImprovementToScenario(
  request: CreateImprovementRequest
): Promise<ScenarioImprovement> {
  const { data: existingImprovements, error: existingError } = await supabase
    .from('scenario_improvements')
    .select('improvement_type')
    .eq('scenario_id', request.scenario_id)
    .eq('improvement_type', request.improvement_type);

  if (existingError) {
    throw new Error('Failed to check existing improvements');
  }

  if (existingImprovements && existingImprovements.length > 0) {
    throw new Error(
      'Ce type de travaux existe déjà dans le scénario. Veuillez le supprimer d\'abord si vous souhaitez le remplacer.'
    );
  }

  const { data: scenario, error: scenarioError } = await supabase
    .from('work_scenarios')
    .select('dpe_id')
    .eq('id', request.scenario_id)
    .maybeSingle();

  if (scenarioError || !scenario) {
    throw new Error('Scenario not found');
  }

  const { data: rawDpeData, error: dpeError } = await supabase
    .from('dpe_data')
    .select('*')
    .eq('id', scenario.dpe_id)
    .maybeSingle();

  if (dpeError || !rawDpeData) {
    throw new Error('DPE data not found');
  }

  const { data: sessionData } = await supabase
    .from('questionnaire_sessions')
    .select('answers_json')
    .eq('dpe_id', rawDpeData.id)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  let answersMap: Record<string, any> = {};
  if (sessionData?.answers_json) {
    answersMap = normalizeAnswers(sessionData.answers_json);
  }

  const normalizedDPE = normalizeDPEData(rawDpeData);
  const enrichedDPE = enrichDPEWithEstimates(normalizedDPE as any);

  const equipment = await fetchEquipmentStandard(request.equipment_standard_id);
  if (!equipment) {
    throw new Error('Equipment standard not found');
  }

  let improvementCalculation = null;

  switch (request.improvement_type) {
    case 'windows':
      improvementCalculation = calculateWindowsImprovement(enrichedDPE, equipment);
      break;
    case 'wall_insulation':
      improvementCalculation = calculateWallInsulationImprovement(enrichedDPE, equipment, answersMap);
      break;
    case 'roof_insulation':
      improvementCalculation = calculateRoofInsulationImprovement(enrichedDPE, equipment);
      break;
    case 'floor_insulation':
      improvementCalculation = calculateFloorInsulationImprovement(enrichedDPE, equipment);
      break;
    case 'heating':
      improvementCalculation = calculateHeatingSystemImprovement(enrichedDPE, equipment);
      break;
    case 'hot_water':
      improvementCalculation = calculateHotWaterSystemImprovement(enrichedDPE, equipment, answersMap);
      break;
    case 'ventilation':
      improvementCalculation = calculateVentilationImprovement(enrichedDPE, equipment);
      break;
    case 'heating_control':
      improvementCalculation = calculateHeatingControlImprovement(enrichedDPE, equipment, answersMap);
      break;
    default:
      throw new Error('Unsupported improvement type');
  }

  if (!improvementCalculation) {
    throw new Error('No improvement possible with this equipment');
  }

  const improvementData: any = {
    scenario_id: request.scenario_id,
    ...improvementCalculation,
  };

  if (improvementCalculation.insulation_coverage_percentage !== undefined) {
    improvementData.insulation_coverage_percentage = improvementCalculation.insulation_coverage_percentage;
  }

  if (improvementCalculation.insulation_coverage_notes !== undefined) {
    improvementData.insulation_coverage_notes = improvementCalculation.insulation_coverage_notes;
  }

  const { energy_type, cop, ...dataWithoutExtraFields } = improvementData;

  if (energy_type !== undefined || cop !== undefined) {
    dataWithoutExtraFields.energy_savings_breakdown = {
      ...(dataWithoutExtraFields.energy_savings_breakdown || {}),
      ...(energy_type !== undefined && { energy_type }),
      ...(cop !== undefined && { cop }),
    };
  }

  const { data, error } = await supabase
    .from('scenario_improvements')
    .insert(dataWithoutExtraFields)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to add improvement: ${error.message}`);
  }

  await recalculateScenarioTotals(request.scenario_id);

  return data;
}

export async function recalculateScenarioTotals(scenarioId: string): Promise<void> {
  const { data: improvements, error: improvementsError } = await supabase
    .from('scenario_improvements')
    .select('*')
    .eq('scenario_id', scenarioId);

  if (improvementsError) {
    throw new Error(`Failed to fetch improvements: ${improvementsError.message}`);
  }

  const { data: scenario, error: scenarioError } = await supabase
    .from('work_scenarios')
    .select('*, dpe_data(*)')
    .eq('id', scenarioId)
    .maybeSingle();

  if (scenarioError || !scenario) {
    throw new Error('Scenario not found');
  }

  const dpe = (scenario as any).dpe_data;

  // Utiliser le calcul cumulatif pour tenir compte de l'interdépendance entre travaux
  const cumulativeResults = recalculateWithCumulativeEffects(dpe, improvements || []);

  console.log('🔄 Comparaison calculs simple vs cumulatif:', {
    scenarioId,
    simpleTotal: improvements?.reduce((sum, imp) => sum + (imp.energy_savings || 0), 0),
    cumulativeTotal: cumulativeResults.totalEnergySavings,
    difference: improvements?.reduce((sum, imp) => sum + (imp.energy_savings || 0), 0) - cumulativeResults.totalEnergySavings
  });

  // IMPORTANT: Ne pas recalculer les classes actuelles du DPE
  // Les classes energy_class et climate_class proviennent du DPE officiel certifié ADEME
  // et doivent être conservées telles quelles pour représenter l'état actuel du logement.

  // Utiliser la consommation du DPE officiel (déjà par m²)
  const currentConsumption = dpe.primary_energy_consumption || dpe.conso_5_usages_par_m2_ep || 0;

  // Utiliser la classe du DPE officiel
  const currentEnergyClass = dpe.energy_class;
  const currentClimateClass = dpe.climate_class;

  const primaryEnergyCalc = calculateProjectedPrimaryEnergy(
    dpe,
    improvements || [],
    dpe.living_area
  );

  const { projectedConsumption, projectedLabel } = calculateProjectedDPELabelFromEP(
    primaryEnergyCalc.projectedEPperM2,
    dpe.living_area
  );

  console.log('✅ Calcul DPE avec nouvelle méthode EP:', {
    scenarioId,
    currentEPperM2: primaryEnergyCalc.currentEPperM2,
    projectedEPperM2: primaryEnergyCalc.projectedEPperM2,
    currentLabel: currentEnergyClass,
    projectedLabel,
    totalEnergySavingsEF: cumulativeResults.totalEnergySavings,
  });

  const gesCalculation = calculateTotalGESWithImprovements(
    dpe,
    improvements || [],
    dpe.living_area
  );

  const projectedEmissions = gesCalculation.projectedTotal;
  const projectedGESLabel = gesCalculation.projectedLabel;

  // Utiliser les émissions GES du DPE officiel (déjà par m²)
  const currentGESEmissions = dpe.greenhouse_gas_emissions || dpe.emission_ges_5_usages_par_m2 || 0;

  const { error: updateError } = await supabase
    .from('work_scenarios')
    .update({
      current_energy_class: currentEnergyClass,
      current_energy_consumption: currentConsumption,
      current_ges_emissions: currentGESEmissions,
      current_climate_class: currentClimateClass,
      total_energy_savings: cumulativeResults.totalEnergySavings,
      total_cost_savings: cumulativeResults.totalCostSavings,
      total_investment: cumulativeResults.totalInvestment,
      payback_period_years: cumulativeResults.paybackPeriod,
      projected_energy_consumption: projectedConsumption,
      projected_energy_class: projectedLabel,
      projected_ges_emissions: projectedEmissions,
      projected_climate_class: projectedGESLabel,
    })
    .eq('id', scenarioId);

  if (updateError) {
    throw new Error(`Failed to update scenario totals: ${updateError.message}`);
  }
}

export async function fetchScenariosByDPE(dpeId: string): Promise<WorkScenario[]> {
  const { data, error } = await supabase
    .from('work_scenarios')
    .select('*')
    .eq('dpe_id', dpeId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch scenarios: ${error.message}`);
  }

  return data || [];
}

export async function fetchScenarioWithImprovements(
  scenarioId: string
): Promise<ScenarioWithImprovements | null> {
  const { data: scenario, error: scenarioError } = await supabase
    .from('work_scenarios')
    .select('*')
    .eq('id', scenarioId)
    .maybeSingle();

  if (scenarioError || !scenario) {
    return null;
  }

  const { data: improvements, error: improvementsError } = await supabase
    .from('scenario_improvements')
    .select('*')
    .eq('scenario_id', scenarioId)
    .order('created_at');

  if (improvementsError) {
    throw new Error(`Failed to fetch improvements: ${improvementsError.message}`);
  }

  return {
    ...scenario,
    improvements: improvements || [],
  };
}

export async function updateScenarioName(scenarioId: string, newName: string): Promise<void> {
  const { error } = await supabase
    .from('work_scenarios')
    .update({ name: newName, updated_at: new Date().toISOString() })
    .eq('id', scenarioId);

  if (error) {
    throw new Error(`Failed to update scenario name: ${error.message}`);
  }
}

export async function deleteImprovement(improvementId: string): Promise<void> {
  const { data: improvement, error: fetchError } = await supabase
    .from('scenario_improvements')
    .select('scenario_id')
    .eq('id', improvementId)
    .maybeSingle();

  if (fetchError || !improvement) {
    throw new Error('Improvement not found');
  }

  const { error } = await supabase
    .from('scenario_improvements')
    .delete()
    .eq('id', improvementId);

  if (error) {
    throw new Error(`Failed to delete improvement: ${error.message}`);
  }

  await recalculateScenarioTotals(improvement.scenario_id);
}

export async function deleteScenario(scenarioId: string): Promise<void> {
  const { error } = await supabase
    .from('work_scenarios')
    .delete()
    .eq('id', scenarioId);

  if (error) {
    throw new Error(`Failed to delete scenario: ${error.message}`);
  }
}

export async function updateScenarioStatus(
  scenarioId: string,
  status: 'draft' | 'saved' | 'archived'
): Promise<void> {
  const { error } = await supabase
    .from('work_scenarios')
    .update({ status })
    .eq('id', scenarioId);

  if (error) {
    throw new Error(`Failed to update scenario status: ${error.message}`);
  }
}